import { makeAutoObservable, runInAction } from 'mobx';
import { Project } from '../shared/models/Project';
import { Banner } from '../shared/models/Banner';
import { ProjectService } from '../services/ProjectService';
import { getBanners } from '../services/BannerService';
import { ProjectListParams, PaginatedResponse } from '../types/api';

/**
 * 项目列表 ViewModel（H5 用户端）
 */
export class ProjectListViewModel {
  /** 项目列表 */
  projects: Project[] = [];
  
  /** 加载状态 */
  loading = false;
  
  /** 错误信息 */
  error: string | null = null;
  
  /** 分页信息 */
  pagination = {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  };
  
  /** 是否还有更多数据 */
  hasMore = true;

  /** Banner（广告位） */
  banners: Banner[] = [];
  bannerLoading = false;

  /** 是否已初始化过列表数据（用于避免重复请求） */
  initialized = false;

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * 加载项目列表
   */
  async loadProjects(params?: Partial<ProjectListParams & { organizationId?: string; organizationType?: string }>) {
    this.loading = true;
    this.error = null;

    try {
      const queryParams: ProjectListParams = {
        page: params?.page || this.pagination.page,
        limit: params?.limit || this.pagination.limit,
      };
      
      // 根据机构类型传递正确的参数
      if (params?.organizationType === 'EXECUTING' && params?.organizationId) {
        queryParams.executingOrganizationId = params.organizationId;
      } else if (params?.organizationId) {
        queryParams.organizationId = params.organizationId;
      }
      
      // 保留其他参数
      if (params?.status) queryParams.status = params.status;
      if (params?.keyword) queryParams.keyword = params.keyword;

      const response: PaginatedResponse<Project> = await ProjectService.getProjects(queryParams);

      runInAction(() => {
        // 仅保留已发布和已停售，已停售排在最后
        const published = (response.list || []).filter((p) => p.status === 'PUBLISHED');
        const stopped = (response.list || []).filter((p) => p.status === 'STOPPED');
        const sortFn = (a: Project, b: Project) => {
          if (a.isPinned !== b.isPinned) return (b.isPinned ? 1 : 0) - (a.isPinned ? 1 : 0);
          const at = new Date(a.updateTime || a.createTime || '').getTime() || 0;
          const bt = new Date(b.updateTime || b.createTime || '').getTime() || 0;
          return bt - at;
        };
        const sortedPublished = published.sort(sortFn);
        const sortedStopped = stopped.sort(sortFn);
        const filteredList = [...sortedPublished, ...sortedStopped];

        if (queryParams.page === 1) {
          // 第一页，替换数据
          this.projects = filteredList;
        } else {
          // 后续页，追加数据
          this.projects = [...this.projects, ...filteredList];
        }
        
        // 使用后端返回的分页信息（基于所有符合条件的项目总数）
        this.pagination = {
          page: response.page,
          limit: response.limit,
          total: response.total,
          totalPages: response.totalPages,
        };
        
        // 根据后端返回的分页信息判断是否还有更多数据
        this.hasMore = response.page < response.totalPages;
        this.loading = false;
        this.initialized = true;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : '加载项目列表失败';
        this.loading = false;
      });
    }
  }

  /**
   * 加载广告位 Banner
   */
  async loadBanners(organizationId?: string) {
    if (this.bannerLoading) return;
    this.bannerLoading = true;
    try {
      const list = await getBanners(organizationId);
      runInAction(() => {
        this.banners = Array.isArray(list) ? list : [];
        this.bannerLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.bannerLoading = false;
      });
    }
  }

  /**
   * 刷新项目列表
   */
  async refresh() {
    this.pagination.page = 1;
    this.hasMore = true;
    await this.loadProjects({ page: 1 });
  }

  /**
   * 加载更多项目
   */
  async loadMore() {
    if (this.loading || !this.hasMore) {
      return;
    }

    await this.loadProjects({
      page: this.pagination.page + 1,
    });
  }

  /**
   * 重置状态
   */
  reset() {
    this.projects = [];
    this.loading = false;
    this.error = null;
    this.pagination = {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    };
    this.hasMore = true;
    this.banners = [];
    this.bannerLoading = false;
    this.initialized = false;
  }

  /**
   * 从首页缓存填充，避免重复请求
   */
  hydrateFromHome(payload: {
    projects: Project[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
    hasMore: boolean;
    banners?: Banner[];
  }) {
    this.projects = payload.projects || [];
    this.pagination = payload.pagination || this.pagination;
    this.hasMore = payload.hasMore;
    this.loading = false;
    this.error = null;
    this.initialized = true;
    if (payload.banners && payload.banners.length > 0) {
      this.banners = payload.banners;
    }
  }
}

